import {Layout} from '@components/layouts/layout'
import {IPluginsProps} from '@components/widgets/PluginsTable'
import {ReactElement} from "react";
import {GetStaticProps} from "next";
import type {NextPageWithLayout} from './_app'
import {LanguageOption, TagOption} from "@components/elements/FiltersToolbar";
import PluginsList, {
    DEFAULT_MAX_ACTIVE_INSTALLS_FILTER_VALUE,
    DEFAULT_MAX_DOWNLOADS_FILTER_VALUE,
    DEFAULT_MAX_ADDED_FILTER_VALUE,
    DEFAULT_MAX_LAST_UPDATED_FILTER_VALUE,
    DEFAULT_MAX_AGE_FILTER_VALUE,
    DEFAULT_MAX_RATING_FILTER_VALUE,
    DEFAULT_MAX_TOTAL_RATINGS_FILTER_VALUE,
    DEFAULT_MIN_ACTIVE_INSTALLS_FILTER_VALUE,
    DEFAULT_MIN_DOWNLOADS_FILTER_VALUE,
    DEFAULT_MIN_ADDED_FILTER_VALUE,
    DEFAULT_MIN_LAST_UPDATED_FILTER_VALUE,
    DEFAULT_MIN_AGE_FILTER_VALUE,
    DEFAULT_MIN_RATING_FILTER_VALUE,
    DEFAULT_MIN_TOTAL_RATINGS_FILTER_VALUE,
    DEFAULT_RESULTS_PER_PAGE,
    DEFAULT_SORT_COLUMN,
    DEFAULT_SORT_DESCENDING,
    IPluginsSearchData
} from "@components/widgets/PluginsList";

const SSR_MAX_DESCRIPTION_LENGTH = 600;
const SSR_MAX_ICONS = 1;

export interface IIndexPageProps {
    pluginsProps: IPluginsProps
}


export const getStaticProps: GetStaticProps<IIndexPageProps> = async () => {
    const renderedAt = Date.now();
    const basePluginsProps: IPluginsProps = {
        searchFilterValue: '',
        nameFilterValue: '',
        descriptionFilterValue: '',
        languagesFilterValues: [],
        tagsFilterValues: [],
        sortColumn: DEFAULT_SORT_COLUMN,
        sortDescending: DEFAULT_SORT_DESCENDING,
        page: 1,
        perPage: DEFAULT_RESULTS_PER_PAGE,
        minRatingFilterValue: DEFAULT_MIN_RATING_FILTER_VALUE,
        maxRatingFilterValue: DEFAULT_MAX_RATING_FILTER_VALUE,
        minDownloadsFilterValue: DEFAULT_MIN_DOWNLOADS_FILTER_VALUE,
        maxDownloadsFilterValue: DEFAULT_MAX_DOWNLOADS_FILTER_VALUE,
        minActiveInstallsFilterValue: DEFAULT_MIN_ACTIVE_INSTALLS_FILTER_VALUE,
        maxActiveInstallsFilterValue: DEFAULT_MAX_ACTIVE_INSTALLS_FILTER_VALUE,
        minTotalRatingsFilterValue: DEFAULT_MIN_TOTAL_RATINGS_FILTER_VALUE,
        maxTotalRatingsFilterValue: DEFAULT_MAX_TOTAL_RATINGS_FILTER_VALUE,
        minLastUpdatedFilterValue: DEFAULT_MIN_LAST_UPDATED_FILTER_VALUE,
        maxLastUpdatedFilterValue: DEFAULT_MAX_LAST_UPDATED_FILTER_VALUE,
        minAgeFilterValue: DEFAULT_MIN_AGE_FILTER_VALUE,
        maxAgeFilterValue: DEFAULT_MAX_AGE_FILTER_VALUE,
        minAddedFilterValue: DEFAULT_MIN_ADDED_FILTER_VALUE,
        maxAddedFilterValue: DEFAULT_MAX_ADDED_FILTER_VALUE,
        languageOptionsJsons: [],
        tagOptionsJsons: [],
        renderedAt,
    };

    const emptySearchData: IPluginsSearchData = {
        tags: [],
        languages: [],
        plugins: [],
        currentPage: basePluginsProps.page ?? 1,
        totalPlugins: 0,
        pluginsPerPage: basePluginsProps.perPage ?? 0,
        totalPages: 0,
        loadingTags: false,
        loadingLanguages: false,
        loadingPlugins: false
    };

    try {
        const yearsToDays = (years?: number) => years && years > 0 ? Math.round(years * 365) : undefined;
        const [{PluginsGetter, PluginsLanguagesGetter, PluginsTagsGetter}, {getDb}, {Plugins}] = await Promise.all([
            import('../server/api/data/PluginsGetter'),
            import('../server/api/db'),
            import('../hooks/plugins/plugins'),
        ]);

        const db = getDb();
        const sharedFilters = {
            db,
            search: basePluginsProps.searchFilterValue,
            name: basePluginsProps.nameFilterValue,
            description: basePluginsProps.descriptionFilterValue,
            languages: basePluginsProps.languagesFilterValues,
            tags: basePluginsProps.tagsFilterValues,
            min_rating: basePluginsProps.minRatingFilterValue,
            max_rating: basePluginsProps.maxRatingFilterValue,
            min_active_installs: basePluginsProps.minActiveInstallsFilterValue,
            max_active_installs: basePluginsProps.maxActiveInstallsFilterValue,
            min_downloads: basePluginsProps.minDownloadsFilterValue,
            max_downloads: basePluginsProps.maxDownloadsFilterValue,
            min_total_ratings: basePluginsProps.minTotalRatingsFilterValue,
            max_total_ratings: basePluginsProps.maxTotalRatingsFilterValue,
            min_last_updated: basePluginsProps.minLastUpdatedFilterValue || undefined,
            max_last_updated: basePluginsProps.maxLastUpdatedFilterValue || undefined,
            min_age_days: (basePluginsProps.minAgeFilterValue ?? 0) > DEFAULT_MIN_AGE_FILTER_VALUE ? yearsToDays(basePluginsProps.minAgeFilterValue) : undefined,
            max_age_days: (basePluginsProps.maxAgeFilterValue ?? DEFAULT_MAX_AGE_FILTER_VALUE) < DEFAULT_MAX_AGE_FILTER_VALUE ? yearsToDays(basePluginsProps.maxAgeFilterValue) : undefined,
            min_added: basePluginsProps.minAddedFilterValue || undefined,
            max_added: basePluginsProps.maxAddedFilterValue || undefined,
            page: basePluginsProps.page,
            per_page: basePluginsProps.perPage,
            order: basePluginsProps.sortDescending ? 'desc' : 'asc',
            orderby: basePluginsProps.sortColumn,
        };

        const [pluginsResponse, tagsResponse, languagesResponse] = await Promise.all([
            new PluginsGetter().get(sharedFilters),
            new PluginsTagsGetter().get(sharedFilters),
            new PluginsLanguagesGetter().get(sharedFilters),
        ]);

        const processedPluginsData = Plugins.processApiResponse(pluginsResponse as any, renderedAt);

        const trimmedPlugins = processedPluginsData.plugins?.map((plugin) => {
            const strippedPlugin = {...plugin};
            strippedPlugin.banners = undefined;
            strippedPlugin.changelog = undefined;
            strippedPlugin.htmlStrippedChangelog = undefined;
            strippedPlugin.description = undefined;
            strippedPlugin.htmlStrippedDescription = strippedPlugin.htmlStrippedDescription?.slice(0, SSR_MAX_DESCRIPTION_LENGTH);
            strippedPlugin.faq = undefined;
            strippedPlugin.htmlStrippedFaq = undefined;
            strippedPlugin.installation = undefined;
            strippedPlugin.htmlStrippedInstallation = undefined;
            strippedPlugin.icons = strippedPlugin.icons?.slice(0, SSR_MAX_ICONS);
            strippedPlugin.reviews = [];
            strippedPlugin.screenshots = undefined;
            strippedPlugin.htmlStrippedScreenshots = undefined;
            return strippedPlugin;
        }) ?? [];

        const tags = (tagsResponse as any)?.data?.map((tag: any) => new TagOption(tag.slug, `${tag.label} (${tag.plugins_count})`, tag.plugins_count)) ?? [];
        const languages = (languagesResponse as any)?.data?.map((language: any) => new LanguageOption(language.slug, `${language.english_name} (${language.plugins_count})`, language.plugins_count)) ?? [];

        const searchData: IPluginsSearchData = {
            tags,
            languages,
            plugins: trimmedPlugins,
            currentPage: processedPluginsData.current_page,
            totalPlugins: processedPluginsData.total_plugins,
            pluginsPerPage: processedPluginsData.plugins_per_page,
            totalPages: processedPluginsData.total_pages,
            loadingTags: false,
            loadingLanguages: false,
            loadingPlugins: false
        };

        return {
            props: {
                pluginsProps: {
                    ...basePluginsProps,
                    languageOptionsJsons: languages.map((language: LanguageOption) => language.toJson()),
                    tagOptionsJsons: tags.map((tag: TagOption) => tag.toJson()),
                    stringifyPluginsSearchData: JSON.stringify(searchData),
                    renderedAt,
                }
            },
            revalidate: false
        };
    } catch (error) {
        console.error('Error building static props for plugins page:', error);
        return {
            props: {
                pluginsProps: {
                    ...basePluginsProps,
                    stringifyPluginsSearchData: JSON.stringify(emptySearchData),
                    renderedAt,
                }
            },
            revalidate: 600
        };
    }
};


// @ts-ignore
const Page: NextPageWithLayout = (props: IIndexPageProps) => {
    return (
        <PluginsList
            searchFilterValue={props.pluginsProps.searchFilterValue}
            nameFilterValue={props.pluginsProps.nameFilterValue}
            descriptionFilterValue={props.pluginsProps.descriptionFilterValue}
            languagesFilterValues={props.pluginsProps.languagesFilterValues}
            tagsFilterValues={props.pluginsProps.tagsFilterValues}
            sortColumn={props.pluginsProps.sortColumn}
            sortDescending={props.pluginsProps.sortDescending}
            page={props.pluginsProps.page}
            perPage={props.pluginsProps.perPage}
            languageOptions={props.pluginsProps.languageOptionsJsons?.map((json: string) => LanguageOption.fromJson(json))}
            tagOptions={props.pluginsProps.tagOptionsJsons?.map((json: string) => TagOption.fromJson(json))}
            minRatingFilterValue={props.pluginsProps.minRatingFilterValue}
            maxRatingFilterValue={props.pluginsProps.maxRatingFilterValue}
            minActiveInstallsFilterValue={props.pluginsProps.minActiveInstallsFilterValue}
            maxActiveInstallsFilterValue={props.pluginsProps.maxActiveInstallsFilterValue}
            minDownloadsFilterValue={props.pluginsProps.minDownloadsFilterValue}
            maxDownloadsFilterValue={props.pluginsProps.maxDownloadsFilterValue}
            minTotalRatingsFilterValue={props.pluginsProps.minTotalRatingsFilterValue}
            maxTotalRatingsFilterValue={props.pluginsProps.maxTotalRatingsFilterValue}
            minLastUpdatedFilterValue={props.pluginsProps.minLastUpdatedFilterValue}
            maxLastUpdatedFilterValue={props.pluginsProps.maxLastUpdatedFilterValue}
            stringifyPluginsSearchData={props.pluginsProps.stringifyPluginsSearchData}
            renderedAt={props.pluginsProps.renderedAt}
        />
    )
}

Page.getLayout = function getLayout(page: ReactElement) {
    const baseUrl = String(process.env.NEXT_PUBLIC_WEBSITE_URL || "");
    const normalizedBaseUrl = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
    const canonicalUrl = normalizedBaseUrl ? `${normalizedBaseUrl}/plugins` : "/plugins";

    return (
        <Layout
            title={"WordPress Plugin Database: Search, Filter, and Sort"}
            description={"Search and filter WordPress plugins by installs, downloads, rating depth, update activity, tags, languages, and maintenance signals."}
            image="/catspyjamas.jpg"
            url={canonicalUrl}
        >
            {page}
        </Layout>
    )
}


export default Page
